/**
 * WorkWise content of step 3 (Infrastructure & Settings): the Customer section
 * that comes first, and the deployment model, server, network, tag and summary
 * sections that replace the generic Pipeline Parameters. Only
 * NewDeploymentPage.module.css classes.
 */
import { FormProvider, useFormContext, useWatch } from 'react-hook-form'
import { DEPLOYMENT_MODEL_LABELS } from '@/config/workwise'
import type { WorkwiseFlow } from '@/hooks/useWorkwiseFlow'
import { useCostEstimate, useSharedPicks } from '@/hooks/useWorkwise'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { WorkwiseFormValues } from '@/types/workwise'
import { describeTopology, vmCount } from '@/utils/workwise/cost'
import { costTotalText } from '@/utils/workwise/costEstimate'
import { INFRA_STEP } from '@/utils/workwise/serverErrors'
import { ErrorList, Row, Section, TextField } from './fields'
import { AvailabilityHint, ExtraTagsField } from './ServerFields'
import { CostEstimateLines, CostEstimateSource } from './WorkwiseCost'
import { NetworkSection } from './WorkwiseNetwork'
import { AppServerSection, DbServerSection, DeploymentModelField, GatewayServerSection } from './WorkwiseServers'

/** Server-side errors that belong to one step, as "path: message". */
export function StepServerErrors({ flow, step }: { flow: WorkwiseFlow; step: number }) {
  const messages = (flow.serverErrors?.fields ?? []).filter((f) => f.step === step).map((f) => `${f.path}: ${f.message}`)
  return <ErrorList title="The server rejected these values:" messages={messages} testId={`ww-server-errors-${step}`} />
}

function CustomerFields({ flow }: { flow: WorkwiseFlow }) {
  const { control } = useFormContext<WorkwiseFormValues>()
  const slug = useWatch({ control, name: 'customer.slug' })
  return (
    <Section title="Customer" testId="ww-customer">
      <StepServerErrors flow={flow} step={INFRA_STEP} />
      <div className={styles.formGrid} style={{ marginTop: flow.serverErrors ? 16 : 0 }}>
        <TextField
          path="customer.name"
          label="Customer Name"
          required
          placeholder="e.g. Acme Foods"
          maxLength={50}
          hint={<>Slug: <span data-testid="ww-slug" style={{ fontFamily: 'monospace' }}>{slug || '—'}</span></>}
        />
        <div>
          <TextField
            path="customer.number"
            label="Customer Number"
            required
            placeholder="e.g. 047"
            maxLength={3}
            inputMode="numeric"
            hint="Exactly 3 digits (001–999); part of the VM names"
          />
          <AvailabilityHint state={flow.availability} />
        </div>
      </div>
    </Section>
  )
}

/** Step 3, first section: Customer Name + Customer Number. */
export function WorkwiseCustomerSection({ flow }: { flow: WorkwiseFlow }) {
  return (
    <FormProvider {...flow.form}>
      <CustomerFields flow={flow} />
    </FormProvider>
  )
}

function TagsSection() {
  return (
    <Section title="Tags" testId="ww-tags">
      <div className={styles.formGrid}>
        <TextField path="tags.owner" label="Owner" required placeholder="name@company.com" />
      </div>
      <div style={{ marginTop: 16 }}>
        <ExtraTagsField />
      </div>
    </Section>
  )
}

/** Servers created vs attached, then the backend cost estimate per server (R7-C) and its total. */
function SummarySection({ flow }: { flow: WorkwiseFlow }) {
  const { control } = useFormContext<WorkwiseFormValues>()
  const values = useWatch({ control }) as WorkwiseFormValues
  const picks = useSharedPicks(values)
  const cost = useCostEstimate(values)
  const { created, attached } = describeTopology(values, picks, flow.sizeInfo)
  const count = vmCount(values)
  return (
    <Section title="Summary" testId="ww-summary">
      <div className={styles.reviewTable}>
        <Row label="Deployment model" value={DEPLOYMENT_MODEL_LABELS[values.deploymentModel] ?? values.deploymentModel} testId="ww-summary-model" />
        <Row label="VMs to create" value={count} testId="ww-summary-vm-count" />
        {created.map((s) => (
          <Row key={`c-${s.role}`} label={`Create · ${s.role === 'db' ? 'database' : s.role}`} value={`${s.name} (${s.detail})`} mono testId={`ww-summary-created-${s.role}`} />
        ))}
        {attached.map((s) => (
          <Row key={`a-${s.role}`} label={`Attach · ${s.role === 'db' ? 'database' : s.role}`} value={`${s.name} (${s.detail})`} mono testId={`ww-summary-attached-${s.role}`} />
        ))}
      </div>
      <div style={{ marginTop: 20 }}>
        <CostEstimateLines state={cost} testId="ww-summary-estimate" />
      </div>
      <div className={styles.costSummary} style={{ marginTop: 14 }}>
        <span className={styles.costLabel}>Estimated Monthly Cost</span>
        <span className={styles.costValue} data-testid="ww-summary-cost">
          {costTotalText(cost)}
        </span>
      </div>
      <CostEstimateSource state={cost} testId="ww-summary-estimate" />
    </Section>
  )
}

/**
 * Step 3, replaces Pipeline Parameters: the Deployment Model (right under CI/CD,
 * same radio look), App, Database and Gateway servers, the derived network, tags
 * and the summary.
 */
export function WorkwiseInfraSections({ flow }: { flow: WorkwiseFlow }) {
  return (
    <FormProvider {...flow.form}>
      <DeploymentModelField />
      <AppServerSection flow={flow} />
      <DbServerSection flow={flow} />
      <GatewayServerSection flow={flow} />
      <NetworkSection />
      <TagsSection />
      <SummarySection flow={flow} />
    </FormProvider>
  )
}
